import { AppTypeId } from '@ps/platform-message-framework/common/AppTypeId';
import { ClientPlatform } from '@ps/platform-message-framework/common/ClientPlatform';
import { ProductId } from '@ps/platform-message-framework/common/ProductId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { canUseDOM } from '@ps/web-core-utils';

import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import fetchCrmFailover from '@swf/core/utils/failovers';

import { Header } from '../enums';

interface CrmZone {
  [zoneId: string]: string[];
}

export interface CrmState {
  zones: CrmZone;
  trackCodeAssigned: boolean;
}

interface CRMModel extends ModelConfig<CrmState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const reducers = {
  addZone: (state: CrmState, payload: CrmZone): CrmState => ({
    ...state,
    zones: { ...state.zones, ...payload }
  }),
  setTrackCode: (state: CrmState): CrmState => ({ ...state, trackCodeAssigned: true })
};

const effects = {
  async fetchZone({ zoneId }: { zoneId: string }, { dispatch, request, rootState }: EffectParams) {
    let contents: string[] = [];
    try {
      if (select.cms.isExternalPartnerSite(rootState)) {
        const idpHeaders: Record<string, string> = select.crm.getKongHeaders(rootState);
        const query = select.crm.getQueryParams(rootState);
        const { apiDomain, marketingApiBasePath = '/martech' } = select.config.get(rootState);
        const { body } = await request<{ zoneContents: Record<string, Record<string, string[]>> }>({
          service: `${marketingApiBasePath}/crmservice/content`,
          http: true,
          httpDomain: apiDomain,
          method: 'GET',
          body: { ...query, zone: zoneId },
          headers: {
            ...idpHeaders
          }
        });
        const { zoneContents } = body;
        contents = await Promise.all(
          zoneContents[zoneId]?.urls.map(async (url) => {
            const res = await fetch(url);
            return await res.text();
          })
        );
      } else {
        const { body } = await request<{ contents: string[] }>({
          service: 'crm.zone',
          body: { zoneId },
          headers: {
            brand: select.localSettings.getRegistrationBrand(rootState)
              ? `${select.localSettings.getRegistrationBrand(rootState)}`
              : `${select.cms.getBrandId(rootState)}`
          }
        });
        ({ contents } = body);
      }
    } catch {
      if (canUseDOM) {
        const failoverContent = await fetchCrmFailover(zoneId);
        contents = failoverContent ? [failoverContent] : [];
      }
    } finally {
      dispatch.crm.addZone({ [zoneId]: contents });
    }
  },
  async assignTrackCode(payload: void, { dispatch, request }: EffectParams) {
    try {
      await request({ service: 'crm.assigntrackcode', body: payload });
      dispatch.crm.setTrackCode();
    } catch {
      // do nothing
    }
  }
};

const emptyZone: string[] = [];

const selectors = (slice: Slicer<CrmState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getZone() {
    return createSelector(
      slice,
      (rootState: RootState, zoneId: string) => zoneId,
      (state, zoneId): string[] => state.zones[zoneId] || emptyZone
    );
  },
  isTrackCodeAssigned() {
    return slice((state: CrmState) => !!state.trackCodeAssigned);
  },
  getKongHeaders() {
    return createSelector(
      (rootState: RootState) => select.session.get(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => select.device.getId(rootState),
      (rootState: RootState) => select.device.getOS(rootState),
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (rootState: RootState) => select.device.getPlatform(rootState),
      ({ installId, idpToken }, site, device, os, brand, platform) => {
        const headers: Record<string, string> = {
          [Header.FiSiteId]: `${site}`,
          [Header.FiDeviceId]: `${device}`,
          [Header.FiOsId]: `${os}`,
          [Header.FiBrandId]: `${brand}`,
          [Header.FiPlatformId]: `${platform}`,
          [Header.FiAppTypeId]: `${AppTypeId.RAM}`,
          [Header.FiProductId]: `${ProductId.POKER}`
        };

        if (idpToken) {
          headers.authorization = `Bearer ${idpToken}`;
        }

        if (installId) {
          headers[Header.FiInstallId] = `${installId}`;
        }

        return headers;
      }
    );
  },
  getQueryParams() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => select.device.getPlatform(rootState),
      (rootState: RootState) => select.country.getCountry(rootState),
      (rootState: RootState) => select.account.getCountry(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.cms.getBrandName(rootState),
      (site, platform, country, userCountry, locale, brand) => ({
        site: Sites[site],
        brand,
        platform: ClientPlatform[platform],
        country: userCountry || country,
        language: locale
      })
    );
  }
});

const crm: CRMModel = {
  state: { trackCodeAssigned: false, zones: {} },
  reducers,
  effects,
  selectors
};

export default crm;
